
import React, { useEffect, useState, useCallback, useRef } from 'react';
import {
  ReactFlow,
  Controls, 
  Background,
  Node,
  Edge,
  ConnectionLineType,
  MarkerType,
  useNodesState,
  useEdgesState,
  addEdge
} from '@xyflow/react';
import '@xyflow/react/dist/style.css';
import { Resource } from '../hooks/useResourceManagement';
import { supabase } from '@/integrations/supabase/client';


interface ResourceFlowChartProps {
  resources: Resource[];
  assignments: any[];
  tasks: any[];
}

const ResourceFlowChart = ({ assignments }: ResourceFlowChartProps) => {
  const [nodes, setNodes, onNodesChange] = useNodesState([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState([]);
  const [selectedNodeId, setSelectedNodeId] = useState<string | null>(null);
  const reactFlowInstance = useRef<any>(null);

  // Create nodes from the assignments prop (which are already filtered for the selected task)
  const createNodesFromAssignments = useCallback(() => {
    if (!assignments || assignments.length === 0) {
      setNodes([]);
      setEdges([]);
      return;
    }

    console.log('Creating nodes from task assignments:', assignments);
    
    // Remove duplicates based on resource_id and role_type combination
    const uniqueResources = assignments.reduce((acc: any[], current) => {
      const existing = acc.find(resource => 
        resource.resource_id === current.resource_id && 
        resource.role_type === current.role_type &&
        resource.name === current.name
      );
      if (!existing) {
        acc.push(current);
      }
      return acc;
    }, []);

    console.log('Unique resources after deduplication:', uniqueResources);
    
    // Group nodes by role type
    const nodesByRoleType: Record<string, any[]> = {
      'project-director': [],
      'project-manager': [],
      'team-lead': [],
      'team-member': []
    };
    
    uniqueResources.forEach(resource => {
      const roleType = resource.role_type || 'team-member';
      if (nodesByRoleType[roleType]) {
        nodesByRoleType[roleType].push(resource);
      } else {
        nodesByRoleType['team-member'].push(resource);
      }
    });
    
    const ROLE_TYPE_SPACING = 200;
    const ROW_SPACING = 120;
    const NODE_WIDTH = 200;
    const NODES_PER_ROW = 15;
    
    let currentY = 50;
    const roleTypeStartPositions = {
      'project-director': currentY,
      'project-manager': 0,
      'team-lead': 0,
      'team-member': 0
    };
    
    // Calculate Y positions for each role type
    Object.keys(roleTypeStartPositions).forEach((roleType, index) => {
      if (index === 0) return;
      
      const prevRoleTypes = Object.keys(roleTypeStartPositions).slice(0, index);
      let maxY = roleTypeStartPositions[prevRoleTypes[0] as keyof typeof roleTypeStartPositions];
      
      prevRoleTypes.forEach(prevRoleType => {
        const prevNodes = nodesByRoleType[prevRoleType] || [];
        if (prevNodes.length > 0) {
          const rows = Math.ceil(prevNodes.length / NODES_PER_ROW);
          const prevRoleMaxY = roleTypeStartPositions[prevRoleType as keyof typeof roleTypeStartPositions] + (rows - 1) * ROW_SPACING;
          maxY = Math.max(maxY, prevRoleMaxY);
        }
      });
      
      roleTypeStartPositions[roleType as keyof typeof roleTypeStartPositions] = maxY + ROLE_TYPE_SPACING;
    });
    
    const flowNodes: Node[] = [];
    
    Object.entries(nodesByRoleType).forEach(([roleType, roleResources]) => {
      if (roleResources.length === 0) return;
      
      const startY = roleTypeStartPositions[roleType as keyof typeof roleTypeStartPositions];
      
      roleResources.forEach((resource, index) => {
        const rowIndex = Math.floor(index / NODES_PER_ROW);
        const positionInRow = index % NODES_PER_ROW;
        
        const totalRowWidth = Math.min(roleResources.length - (rowIndex * NODES_PER_ROW), NODES_PER_ROW) * NODE_WIDTH;
        const startX = -totalRowWidth / 2;
        const positionX = startX + (positionInRow * NODE_WIDTH) + (NODE_WIDTH / 2);
        
        const positionY = startY + (rowIndex * ROW_SPACING);
        
        const roleToDisplay = resource.role || 'Role not specified';
        
        let nodeStyle: React.CSSProperties;
        let nodeClass: string;
        
        switch(roleType) {
          case 'project-director':
            nodeStyle = {
              background: 'linear-gradient(135deg, #8B5CF6, #7C3AED)',
              color: 'white',
              border: '3px solid #7C3AED',
              borderRadius: '12px',
              padding: '12px',
              width: '200px',
              textAlign: 'center',
              boxShadow: '0 8px 32px rgba(139, 92, 246, 0.3)',
              fontWeight: 'bold'
            };
            nodeClass = 'project-director-node';
            break;
          case 'project-manager':
            nodeStyle = {
              background: 'linear-gradient(135deg, #3B82F6, #2563EB)',
              color: 'white',
              border: '3px solid #2563EB',
              borderRadius: '10px',
              padding: '10px',
              width: '180px',
              textAlign: 'center',
              boxShadow: '0 6px 24px rgba(59, 130, 246, 0.3)',
              fontWeight: '600'
            };
            nodeClass = 'project-manager-node';
            break;
          case 'team-lead':
            nodeStyle = {
              background: 'linear-gradient(135deg, #10B981, #059669)',
              color: 'white',
              border: '2px solid #059669',
              borderRadius: '8px',
              padding: '10px',
              width: '160px',
              textAlign: 'center',
              boxShadow: '0 4px 16px rgba(16, 185, 129, 0.3)',
              fontWeight: '500'
            };
            nodeClass = 'team-lead-node';
            break;
          default:
            nodeStyle = {
              background: 'linear-gradient(135deg, #F59E0B, #D97706)',
              color: 'white',
              border: '2px solid #D97706',
              borderRadius: '6px',
              padding: '8px',
              width: '140px',
              textAlign: 'center',
              boxShadow: '0 4px 12px rgba(245, 158, 11, 0.3)',
              fontWeight: '400'
            };
            nodeClass = 'team-member-node';
            break;
        }
        
        flowNodes.push({
          id: `${resource.resource_id}-${roleType}`,
          type: 'default',
          position: { 
            x: positionX, 
            y: positionY 
          },
          data: {
            label: (
              <div>
                <div className="text-sm font-medium">{resource.name}</div>
                <div className="text-xs">{roleToDisplay}</div>
                <div className="text-xs opacity-70">{roleType.replace('-', ' ')}</div>
              </div>
            ),
            role_type: roleType,
            assignmentData: resource
          },
          style: nodeStyle,
          className: `${nodeClass} custom-resource-node`,
          draggable: true,
          selectable: true
        });
      });
    });
    
    console.log('Setting flow nodes:', flowNodes);
    setNodes(flowNodes);
    
    // No edges - remove all connecting lines
    setEdges([]);
    
    setTimeout(() => {
      if (reactFlowInstance.current && flowNodes.length > 0) {
        reactFlowInstance.current.fitView({ padding: 0.2, maxZoom: 1 });
      }
    }, 100);
  }, [assignments, setNodes, setEdges]);

  // Create nodes when assignments change
  useEffect(() => {
    console.log('Assignments changed, creating flow nodes:', assignments);
    createNodesFromAssignments();
  }, [createNodesFromAssignments]);

  useEffect(() => {
    console.log('Assignments changed, creating flow nodes:', assignments);
    createNodesFromAssignments();
  }, [createNodesFromAssignments]);

  // Derive reporting-line edges by looking up supervisor_id in users table
  useEffect(() => {
    const buildReportingEdges = async () => {
      if (!nodes || nodes.length === 0) {
        setEdges([]);
        return;
      }

      // Build email -> nodeId map from current nodes
      const emailToNodeId = new Map<string, string>();
      nodes.forEach((n) => {
        const data: any = n.data;
        const email = data?.assignmentData?.email;
        if (email) emailToNodeId.set(String(email).toLowerCase(), n.id);
      });

      const emails = Array.from(emailToNodeId.keys());
      if (emails.length === 0) {
        setEdges([]);
        return;
      }

      try {
        const { data: userRows, error } = await supabase
          .from('users')
          .select('id, email, supervisor_id')
          .in('email', emails);
        if (error) {
          console.error('ResourceFlowChart: users lookup error', error);
          setEdges([]);
          return;
        }

        const emailToUser = new Map<string, { id: string; supervisor_id: string | null }>();
        const idToEmail = new Map<string, string>();
        (userRows || []).forEach((u: any) => {
          if (u?.email) {
            const e = String(u.email).toLowerCase();
            emailToUser.set(e, { id: u.id, supervisor_id: u.supervisor_id });
            idToEmail.set(u.id, e);
          }
        });

        // Fetch supervisor emails not already in the set
        const supervisorIds = Array.from(
          new Set(
            (userRows || [])
              .map((u: any) => u.supervisor_id)
              .filter((sid: any) => sid && !idToEmail.has(sid))
          )
        ) as string[];
        if (supervisorIds.length > 0) {
          const { data: supRows } = await supabase
            .from('users')
            .select('id, email')
            .in('id', supervisorIds);
          (supRows || []).forEach((u: any) => {
            if (u?.email) idToEmail.set(u.id, String(u.email).toLowerCase());
          });
        }

        const newEdges: Edge[] = [];
        emailToUser.forEach((info, email) => {
          if (!info.supervisor_id) return;
          const supEmail = idToEmail.get(info.supervisor_id);
          if (!supEmail) return;
          const sourceNodeId = emailToNodeId.get(supEmail); // supervisor (top)
          const targetNodeId = emailToNodeId.get(email);    // subordinate (bottom)
          if (!sourceNodeId || !targetNodeId || sourceNodeId === targetNodeId) return;
          newEdges.push({
            id: `reports-${sourceNodeId}->${targetNodeId}`,
            source: sourceNodeId,
            target: targetNodeId,
            type: 'smoothstep',
            animated: false,
            style: { stroke: '#FACC15', strokeWidth: 2 },
            markerStart: { type: MarkerType.ArrowClosed, color: '#FACC15' },
          });
        });

        setEdges(newEdges);
      } catch (e) {
        console.error('ResourceFlowChart: buildReportingEdges error', e);
        setEdges([]);
      }
    };

    buildReportingEdges();
  }, [nodes, setEdges]);

  const handleNodeClick = useCallback((event: React.MouseEvent, node: Node) => {
    console.log('Node clicked:', node);
    setSelectedNodeId(node.id);
  }, []);

  const onConnect = useCallback((params: any) => {
    setEdges((eds) => addEdge(params, eds));
  }, [setEdges]);

  const getSelectedNodeInfo = () => {
    if (!selectedNodeId) return null;
    
    const selectedNode = nodes.find(node => node.id === selectedNodeId);
    if (!selectedNode) return null;
    
    return selectedNode.data.assignmentData;
  };

  if (!assignments || assignments.length === 0) {
    return (
      <div style={{ height: 500, width: '100%' }} className="flex items-center justify-center">
        <div className="text-white">No assigned resources to display for this task</div>
      </div>
    );
  }

  const selectedNodeData = getSelectedNodeInfo();

  return (
    <div style={{ height: 500, width: '100%', position: 'relative' }}>
      <style>
        {`
          .custom-resource-node.selected {
            transform: scale(1.05);
            z-index: 1000;
          }
          .custom-resource-node:focus {
            outline: none !important;
            transform: scale(1.05);
            z-index: 1000;
          }
          .custom-resource-node:hover {
            transform: scale(1.02);
            transition: transform 0.2s ease-in-out;
          }
        `}
      </style>
      <ReactFlow
        ref={reactFlowInstance}
        nodes={nodes}
        edges={edges}
        onNodesChange={onNodesChange}
        onEdgesChange={onEdgesChange}
        onConnect={onConnect}
        onNodeClick={handleNodeClick}
        onInit={(instance) => {
          reactFlowInstance.current = instance;
          if (nodes.length > 0) {
            setTimeout(() => {
              instance.fitView({ padding: 0.2, maxZoom: 1 });
            }, 100);
          }
        }}
        fitView={false}
        connectionLineType={ConnectionLineType.SmoothStep}
        nodesDraggable={true}
        nodesConnectable={true}
        elementsSelectable={true}
        panOnDrag={true}
        zoomOnScroll={true}
        preventScrolling={false}
        defaultViewport={{ x: 0, y: 0, zoom: 1 }}
        minZoom={0.1}
        maxZoom={2}
      >
        <Controls />
        <Background color="#aaa" gap={16} />
      </ReactFlow>
      
      {/* Resource Information Display */}
      {selectedNodeData && (
        <div className="absolute top-4 right-4 bg-black text-white p-2 rounded shadow-lg z-10" style={{ fontSize: '10px', width: '200px' }}>
          <div><strong>Name:</strong> {selectedNodeData.name || 'N/A'}</div>
          <div><strong>Role:</strong> {selectedNodeData.role || 'N/A'}</div>
          <div><strong>Role Type:</strong> {selectedNodeData.role_type || 'N/A'}</div>
          <div><strong>Resource ID:</strong> {selectedNodeData.resource_id || 'N/A'}</div>
        </div>
      )}
    </div>
  );
};

export default ResourceFlowChart;
